import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import loyaltyService from '../../services/loyaltyService';
import { Users, Search, UserPlus, ChevronLeft, ChevronRight } from 'lucide-react';

/*
 * Members — the register, plus the self-service sign-ups queue. A sign-up
 * (lead) is a claim typed into a phone at a table; converting it dedupes
 * against the customer register by phone, so nobody ends up with two
 * memberships because they joined twice.
 */

const fmtPts = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const STATUS_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  SUSPENDED: 'bg-amber-100 text-amber-800',
  CLOSED: 'bg-gray-100 text-gray-500',
};

export default function MembersPage() {
  const { t } = useT();
  const { toast } = useToast();
  const { formatDate } = useAppSettings();

  const [tab, setTab] = useState('members');   // members | leads
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Enrol dialog state — one flat form, phone is the key.
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [form, setForm] = useState({ phone: '', first_name: '', last_name: '', email: '', birth_date: '' });

  const load = useCallback((query, pageNo) => {
    setLoading(true);
    loyaltyService.listMembers({ q: query || undefined, page: pageNo, page_size: 20 })
      .then(setData)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  const loadLeads = useCallback(() => {
    loyaltyService.listLeads('PENDING')
      .then(setLeads)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [toast]);

  useEffect(() => { load('', 1); loadLeads(); }, [load, loadLeads]);

  const search = (e) => {
    e?.preventDefault();
    setPage(1);
    load(q, 1);
  };

  const goto = (p) => { setPage(p); load(q, p); };

  const enrol = async (e) => {
    e?.preventDefault();
    setEnrolBusy(true);
    try {
      const m = await loyaltyService.enrol({
        phone: form.phone, first_name: form.first_name, last_name: form.last_name,
        email: form.email || undefined, birth_date: form.birth_date || undefined,
      });
      toast({
        title: m.created ? `${m.name} enrolled` : `${m.name} was already a member`,
        description: `${m.membership_number} · ${fmtPts(m.points_balance)} pts`,
      });
      setEnrolOpen(false);
      setForm({ phone: '', first_name: '', last_name: '', email: '', birth_date: '' });
      load(q, page);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setEnrolBusy(false); }
  };

  const convertLead = async (lead) => {
    try {
      const m = await loyaltyService.convertLead(lead.id);
      toast({ title: `${m.name} is now a member`, description: m.membership_number });
      loadLeads(); load(q, page);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const discardLead = async (lead) => {
    try {
      await loyaltyService.discardLead(lead.id);
      loadLeads();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <Users className="h-7 w-7 text-amber-600" /> {t('loyalty.members')}
          </h1>
        </div>
        <Button onClick={() => setEnrolOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          <UserPlus size={15} className="mr-1" /> {t('loyalty.enrol.action')}
        </Button>
      </div>

      {/* Tabs: the register, and the sign-ups waiting on a decision. */}
      <div className="mb-4 flex gap-1 border-b">
        {[['members', `${t('loyalty.members')}${data ? ` (${data.count})` : ''}`],
          ['leads', `${t('loyalty.leads.title')}${leads.length ? ` (${leads.length})` : ''}`]]
          .map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === key ? 'border-amber-600 text-amber-700 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {label}
            </button>
          ))}
      </div>

      {tab === 'members' && (
        <div className="space-y-4">
          <Card className="p-4">
            <form onSubmit={search} className="flex gap-2 max-w-lg">
              <Input placeholder={t('loyalty.search.placeholder')} value={q}
                onChange={(e) => setQ(e.target.value)} />
              <Button type="submit" disabled={loading}
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
                <Search size={15} className="mr-1" /> {t('loyalty.search.action')}
              </Button>
            </form>
          </Card>

          <Card className="p-5">
            {loading && <p className="text-sm text-gray-500">{t('loyalty.common.loading')}</p>}
            {!loading && data && data.results.length === 0 && (
              <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
            )}
            {!loading && data && data.results.length > 0 && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.name')}</th>
                        <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.phone')}</th>
                        <th className="text-left font-medium py-2 pr-4">{t('loyalty.member.tier')}</th>
                        <th className="text-right font-medium py-2 pr-4">{t('loyalty.member.balance')}</th>
                        <th className="text-right font-medium py-2 pr-4">{t('loyalty.member.lifetime')}</th>
                        <th className="text-right font-medium py-2 pr-4">{t('loyalty.member.visits')}</th>
                        <th className="text-left font-medium py-2">{t('loyalty.common.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.results.map((m) => (
                        <tr key={m.id}>
                          <td className="py-2 pr-4">
                            <Link to={`/loyalty/members/${m.id}`}
                              className="font-medium text-gray-900 hover:underline">{m.name}</Link>
                            <div className="font-mono text-[11px] text-gray-400">{m.membership_number}</div>
                          </td>
                          <td className="py-2 pr-4">{m.phone}</td>
                          <td className="py-2 pr-4">
                            {m.tier ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: m.tier.colour || '#64748B' }}>{m.tier.name}</span>
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums">{fmtPts(m.points_balance)}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">{fmtPts(m.lifetime_points)}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">{m.visits}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[m.status] || 'bg-gray-100 text-gray-600'}`}>
                              {m.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.total_pages > 1 && (
                  <div className="mt-3 flex items-center justify-end gap-2 text-sm text-gray-600">
                    <button type="button" disabled={page <= 1} onClick={() => goto(page - 1)}
                      className="p-1 rounded hover:bg-accent disabled:opacity-40"><ChevronLeft size={16} /></button>
                    <span className="tabular-nums">{page} / {data.total_pages}</span>
                    <button type="button" disabled={page >= data.total_pages} onClick={() => goto(page + 1)}
                      className="p-1 rounded hover:bg-accent disabled:opacity-40"><ChevronRight size={16} /></button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      {tab === 'leads' && (
        <Card className="p-5">
          {leads.length === 0 ? (
            <p className="text-sm text-gray-500">{t('loyalty.leads.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.name')}</th>
                    <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.phone')}</th>
                    <th className="text-left font-medium py-2 pr-4">Email</th>
                    <th className="text-left font-medium py-2 pr-4">Source</th>
                    <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.when')}</th>
                    <th className="text-right font-medium py-2">{t('loyalty.common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((l) => (
                    <tr key={l.id}>
                      <td className="py-2 pr-4 font-medium text-gray-900">{l.name}</td>
                      <td className="py-2 pr-4">{l.phone}</td>
                      <td className="py-2 pr-4">{l.email || '—'}</td>
                      <td className="py-2 pr-4">{l.source}</td>
                      <td className="py-2 pr-4">{formatDate(l.created_at)}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <button type="button" onClick={() => convertLead(l)}
                          className="text-sm font-semibold text-emerald-700 hover:underline mr-3">
                          {t('loyalty.leads.convert')}
                        </button>
                        <button type="button" onClick={() => discardLead(l)}
                          className="text-sm text-gray-500 hover:underline">
                          {t('loyalty.leads.discard')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Enrol dialog — kept as a simple card overlay, matching the app's
          lighter-weight dialogs; the phone number is the whole key. */}
      {enrolOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !enrolBusy && setEnrolOpen(false)}>
          <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900">{t('loyalty.enrol.title')}</h2>
            <p className="text-xs text-gray-500 mt-1 mb-4">{t('loyalty.enrol.hint')}</p>
            <form onSubmit={enrol} className="space-y-3">
              <Input placeholder={`${t('loyalty.common.phone')} *`} inputMode="numeric" required
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="First name *" required value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                <Input placeholder="Last name" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <Input placeholder="Email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <div>
                <label className="text-xs text-gray-500">Birthday (for the birthday bonus)</label>
                <Input type="date" value={form.birth_date}
                  onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" disabled={enrolBusy}
                  onClick={() => setEnrolOpen(false)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" disabled={enrolBusy}
                  className="bg-amber-600 hover:bg-amber-700 text-white">
                  {t('loyalty.enrol.action')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
